/**
 * External dependencies
 */

import { colord, extend, getFormat } from 'colord';
import namesPlugin from 'colord/plugins/names';

/**
 * WordPress dependencies
 */
import { useCallback, useState, useMemo } from '@wordpress/element';
import { useDebounce } from '@wordpress/compose';
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import { useContextSystem, contextConnect } from '../context';
import { ColorfulWrapper, SelectControl, AuxiliaryColorArtefactWrapper, AuxiliaryColorArtefactHStackHeader, ColorInputWrapper } from './styles';
import { ColorCopyButton } from './color-copy-button';
import { ColorInput } from './color-input';
import { Picker } from './picker';
import { useControlledValue } from '../utils/hooks';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
extend([namesPlugin]);
const options = [{
  label: 'RGB',
  value: 'rgb'
}, {
  label: 'HSL',
  value: 'hsl'
}, {
  label: 'Hex',
  value: 'hex'
}];
const UnconnectedColorPicker = (props, forwardedRef) => {
  const {
    enableAlpha = false,
    color: colorProp,
    onChange,
    defaultValue = '#fff',
    copyFormat,
    ...divProps
  } = useContextSystem(props, 'ColorPicker');

  // Use a safe default value for the color and remove the possibility of `undefined`.
  const [color, setColor] = useControlledValue({
    onChange,
    value: colorProp,
    defaultValue
  });
  const safeColordColor = useMemo(() => {
    return colord(color || '');
  }, [color]);
  const debouncedSetColor = useDebounce(setColor);
  const handleChange = useCallback(nextValue => {
    debouncedSetColor(nextValue.toHex());
  }, [debouncedSetColor]);
  const [colorType, setColorType] = useState(copyFormat || 'hex');

  /*
   * ! Listener intended for the CAPTURE phase
   *
   * Capture paste events over the entire color picker, looking for clipboard
   * data that could be parsed as a color. If not, let the paste event
   * propagate normally, so that individual input controls within the
   * component have a chance to handle it.
   */
  const maybeHandlePaste = useCallback(event => {
    const pastedText = event.clipboardData?.getData('text')?.trim();
    if (!pastedText) {
      return;
    }
    const parsedColor = colord(pastedText);
    if (!parsedColor.isValid()) {
      return;
    }

    // Apply all valid colors, even if the format isn't supported in
    // the UI (e.g. names like "cyan" or, in the future color spaces
    // like "lch" if we add the right colord plugins)
    handleChange(parsedColor);

    // This redundancy helps TypeScript and is safer than assertions
    const supportedFormats = {
      hex: 'hex',
      rgb: 'rgb',
      hsl: 'hsl'
    };
    const detectedFormat = String(getFormat(pastedText));
    const newColorType = supportedFormats[detectedFormat];
    if (newColorType) {
      setColorType(newColorType);
    }

    // Stop at capture phase; no bubbling
    event.stopPropagation();
    event.preventDefault();
  }, [handleChange, setColorType]);
  return /*#__PURE__*/_jsxs(ColorfulWrapper, {
    ref: forwardedRef,
    ...divProps,
    onPasteCapture: maybeHandlePaste,
    children: [/*#__PURE__*/_jsx(Picker, {
      onChange: handleChange,
      color: safeColordColor,
      enableAlpha: enableAlpha
    }), /*#__PURE__*/_jsxs(AuxiliaryColorArtefactWrapper, {
      children: [/*#__PURE__*/_jsxs(AuxiliaryColorArtefactHStackHeader, {
        justify: "space-between",
        children: [/*#__PURE__*/_jsx(SelectControl, {
          __nextHasNoMarginBottom: true,
          size: "compact",
          options: options,
          value: colorType,
          onChange: nextColorType => setColorType(nextColorType),
          label: __('Color format'),
          hideLabelFromVision: true,
          variant: "minimal"
        }), /*#__PURE__*/_jsx(ColorCopyButton, {
          color: safeColordColor,
          colorType: copyFormat || colorType
        })]
      }), /*#__PURE__*/_jsx(ColorInputWrapper, {
        direction: "column",
        gap: 2,
        children: /*#__PURE__*/_jsx(ColorInput, {
          colorType: colorType,
          color: safeColordColor,
          onChange: handleChange,
          enableAlpha: enableAlpha
        })
      })]
    })]
  });
};
export const ColorPicker = contextConnect(UnconnectedColorPicker, 'ColorPicker');
export default ColorPicker;